/**
 * The search params Stripe Checkout returns with (billing.ts success_url and
 * cancel_url), as a zod shape the Usage and Billing route schemas spread into
 * their own. Kept apart from TopupReturnNotice, and importing only zod and the
 * shared helpers, so the router can validate them without loading the page
 * code. A malformed value is dropped, as with every search helper.
 */
import { z } from 'zod';
import { oneOf } from '../../navigation/searchParams';

const CHECKOUT_SESSION_ID = /^cs_[A-Za-z0-9_]{1,250}$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Text matching `re`, else unset. Only real text: a JSON-parsed number or array is dropped. */
const textMatching = (re: RegExp) =>
  z
    .string()
    .refine((v) => re.test(v))
    .optional()
    .catch(undefined);

export const topupReturnShape = {
  topup: oneOf(['success', 'canceled']),
  /** On a success: the Checkout session, which the notice polls until the credit lands. */
  session_id: textMatching(CHECKOUT_SESSION_ID),
  /** On a cancel: the checkout's cancel ref, which POST /billing/topup-cancel closes it by. */
  ref: textMatching(UUID_RE),
};

/** Keep only a well-formed top-up return from the address. */
export const topupReturnSearch = z.object(topupReturnShape);

export type TopupReturnSearch = z.infer<typeof topupReturnSearch>;

/** The address's search without the top-up return params, for cleaning the address once the return is read. */
export function withoutTopupReturn<T extends object>(search: T): Omit<T, keyof TopupReturnSearch> {
  const { topup: _topup, session_id: _sessionId, ref: _ref, ...rest } = search as T & TopupReturnSearch;
  return rest;
}
